<template>
  <div
    ref="componentContainer"
    class="component_container h-100"
    style="overflow: auto;"
    :id="'comp-' + componentNumber + '-div'"
  >
    <div v-for="(dataTable, idx) in spotTable" :key="idx">
      <div class="row f-row" style="height: 2.2rem;">
        <div class="col col-auto d-flex align-items-center">
          <span class="ml-2 mr-1" style="color: var(--textactive);">{{
            dataTable["name"]
          }}</span>
        </div>
      </div>

      <!-- Table -->
      <div v-if="dataTable['data'].length == 0" class="component_flexible">
        <SkeletonSpotTable :showFields="showFields" />
      </div>
      <DataTable
        v-else
        columnResizeMode="expand"
        class="p-datatable-sm overview_table"
        resizableColumns
        scrollable
        :key="showFields ? 'full' : 'compact'"
        :scrollHeight="tableHeight[idx] + 'px'"
        :value="dataTable.data"
      >
        <Column
          field="asset"
          header="Asset"
          sortable
          frozen
          style="min-width: 120px"
        >
          <template #body="{ data }">
            <div
              class="d-flex align-items-center h-100 w-100"
              :class="{ ovPadding: TAVisible == -1 }"
              @mouseover="onHoverHelp(data.description, $event)"
              @mouseleave="onHoverHelp(null, $event)"
            >
              <ButtonView
                v-if="TAVisible > -1"
                fontSize="12"
                :showBorder="false"
                @btnAbort="
                  () => onUserShowTAChart(data.assetType, data.asset, data.term)
                "
              />
              {{ data.asset }}
              <span v-if="data.term && data.term != '0'" class="ml-1">
                ({{ data.term }})</span
              >
            </div>
          </template>
        </Column>

        <Column field="close" header="Last" sortable style="min-width: 100px">
          <template #body="{ data, index }">
            <div
              class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
              :class="{
                row_up: animateUp.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                ),
                row_down: animateDown.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                )
              }"
            >
              <span
                :class="arrowClass(data.change)"
                v-if="data.change !== 0"
              ></span>
              <span class="ml-1"> {{ priceFormat(data.close) }}</span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          sortable
          field="change"
          header="Change"
          style="min-width: 100px"
        >
          <template #body="{ data, index }">
            <div
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: animateUp.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                ),
                row_down: animateDown.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                )
              }"
            >
              <span
                :class="{ dayUp: data.change > 0, dayDown: data.change < 0 }"
              >
                {{ changeFormat(data.change, data.close) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          field="pct_change"
          header="% Chg"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data, index }">
            <div
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: animateUp.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                ),
                row_down: animateDown.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                )
              }"
            >
              <span
                :class="{ dayUp: data.change > 0, dayDown: data.change < 0 }"
              >
                {{ percentageFormat(data.pct_change) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="pct_change_5"
          header="% 5D"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data, index }">
            <div
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: animateUp.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                ),
                row_down: animateDown.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                )
              }"
            >
              <span
                :class="{
                  dayUp: data.pct_change_5 > 0,
                  dayDown: data.pct_change_5 < 0
                }"
              >
                {{ percentageFormat(data.pct_change_5) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="pct_change_10"
          header="% 10D"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data, index }">
            <div
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: animateUp.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                ),
                row_down: animateDown.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                )
              }"
            >
              <span
                :class="{
                  dayUp: data.pct_change_10 > 0,
                  dayDown: data.pct_change_10 < 0
                }"
              >
                {{ percentageFormat(data.pct_change_10) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="pct_change_20"
          header="% 20D"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data, index }">
            <div
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: animateUp.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                ),
                row_down: animateDown.includes(
                  idx == 0
                    ? index
                    : idx == 1
                    ? index + spotTable[0]['data'].length
                    : index +
                      spotTable[0]['data'].length +
                      spotTable[1]['data'].length
                )
              }"
            >
              <span
                :class="{
                  dayUp: data.pct_change_20 > 0,
                  dayDown: data.pct_change_20 < 0
                }"
              >
                {{ percentageFormat(data.pct_change_20) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="open"
          header="Open"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div class="d-flex align-items-center h-100 w-100 ovPadding">
              {{ priceFormat(data.open) }}
            </div>
          </template>
        </Column>

        <Column field="high" header="High" style="min-width: 100px">
          <template #body="{ data }">
            <div class="d-flex align-items-center h-100 w-100 ovPadding">
              {{ priceFormat(data.high) }}
            </div>
          </template>
        </Column>

        <Column field="low" header="Low" style="min-width: 100px">
          <template #body="{ data }">
            <div class="d-flex align-items-center h-100 w-100 ovPadding">
              {{ priceFormat(data.low) }}
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="prev_close"
          header="Previous"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div class="d-flex align-items-center h-100 w-100 ovPadding">
              {{ priceFormat(data.prev_close) }}
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="observation_date"
          header="Updated"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div class="d-flex align-items-center h-100 w-100 ovPadding">
              {{ dateFormat(data.observation_date) }}
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import ButtonView from "@/components/buttons/ButtonView.vue";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import SkeletonSpotTable from "@/components/overview/SkeletonSpotTable";
import {
  darkBackground,
  grayLightBackgroundAlpha2,
  lightBackground,
  textactive
} from "@/_core/_helpers/_parametric_tools";
import { format, select, timeFormat } from "d3";

export default {
  name: "SpotTable",
  props: {
    animateUp: { required: true, default: () => [] },
    animateDown: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    componentNumber: { required: true, default: 1 },
    spotTable: { required: true, default: () => [] },
    showFields: { required: true, type: Boolean, default: false },
    TAVisible: { required: false, type: Number, default: -1 }
  },
  components: {
    ButtonView,
    Column,
    DataTable,
    SkeletonSpotTable
  },
  data() {
    return {
      showTooltip: false,
      containerHeight: 200,
      tooltip: null
    };
  },
  mounted() {
    const id = `#comp-${this.componentNumber}-div`;
    this.tooltip = select(id)
      .append("div")
      .style("opacity", 0)
      .style("margin", 0)
      .style("padding", "8px")
      .style("text-align", "center")
      .style("background", darkBackground(this.colorTheme))
      .style("border", `1px solid ${lightBackground(this.colorTheme)}`)
      .style(
        "box-shadow",
        `0 4px 12px ${grayLightBackgroundAlpha2(this.colorTheme)}`
      )
      .style("border-radius", "6px")
      .style("color", textactive(this.colorTheme))
      .attr("class", "tooltip")
      .style("font-size", "0.8rem")
      .style("width", "220px")
      .html("");
    this.updateHeight();
    window.addEventListener("resize", this.updateHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateHeight);
  },
  computed: {
    tableHeight() {
      let hei = [];
      let localHei = 0;
      let totalRows = 0;
      for (const table of this.spotTable) {
        localHei = table.data.length || 1;
        totalRows += localHei;
        hei.push(localHei);
      }
      const result = hei.map(h => {
        const proportional = (h / totalRows) * this.containerHeight;
        return Math.max(315, proportional);
      });
      return result;
    }
  },
  watch: {
    colorTheme() {
      this.tooltip
        .style("background", darkBackground(this.colorTheme))
        .style("border", `1px solid ${lightBackground(this.colorTheme)}`)
        .style(
          "box-shadow",
          `0 4px 12px ${grayLightBackgroundAlpha2(this.colorTheme)}`
        )
        .style("color", textactive(this.colorTheme));
    }
  },
  methods: {
    onUserShowTAChart(assetType, asset, term) {
      this.$emit("eventOnUserShowTAChart", {
        assetType: assetType,
        irAvailableTerms: [{ label: "...", value: "..." }],
        selectedAsset: asset,
        selectedTerm: term,
        continuous_contract: null,
        showFields: null,
        componentNumber: this.componentNumber
      });
    },
    updateHeight() {
      const el = this.$refs.componentContainer;
      if (el) {
        this.containerHeight = el.offsetHeight; // o el.clientHeight
      }
    },
    priceFormat(val) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(val) > 10000
        ? format(",.0f")(val)
        : Math.abs(val) > 100
        ? format(",.2f")(val)
        : Math.abs(val) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    changeFormat(val, price) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(price) > 10000
        ? format(",.0f")(val)
        : Math.abs(price) > 100
        ? format(",.2f")(val)
        : Math.abs(price) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    percentageFormat(val) {
      if (val == null || !isFinite(val) || val === 0) return "-";
      return format(",.2%")(val);
    },
    dateFormat(val) {
      if (!val) return "";
      const dateVal = new Date(val);
      const today = new Date();
      const sameDay = dateVal.toDateString() === today.toDateString();
      if (sameDay && dateVal.getHours() === 0 && dateVal.getMinutes() === 0) {
        return timeFormat("%d%b%y")(dateVal);
      }
      return sameDay
        ? timeFormat("%H:%M")(dateVal)
        : timeFormat("%d%b%y")(dateVal);
    },
    arrowClass(value) {
      if (value > 0) return "dayUp fa fa-play rotateUp";
      if (value < 0) return "dayDown fa fa-play rotateDown";
      return "";
    },
    onUserClearComponent() {
      this.$emit("eventOnUserClearComponent", this.componentNumber);
    },
    onHoverHelp(val, event) {
      if (val != null) {
        event.preventDefault();
        const id = `#comp-${this.componentNumber}-div`;
        const rect = select(id)
          .node()
          .getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        this.tooltip
          .style("opacity", 1)
          .style("position", "absolute")
          .style("pointer-events", "none")
          .style("left", `${x + 12}px`)
          .style("top", `${y + 40}px`)
          .html(val);
      } else {
        this.tooltip.style("opacity", 0).html("");
      }
    }
  },
  filters: {}
};
</script>
<style scoped>
.min_w {
  min-width: 60px;
}

.dayUp {
  color: var(--symmetry-h);
}
.rotateUp {
  transform: rotate(270deg) !important;
}

.dayDown {
  color: var(--red);
}
.rotateDown {
  transform: rotate(90deg) !important;
}

.row_up {
  background-color: var(--expo-alpha-3);
  transition: background-color 0.5s ease !important;
}

.row_down {
  background-color: var(--red-alpha-3);
  transition: background-color 0.5s ease !important;
}

.row_up {
  animation: pulse-up 0.5s ease-in-out;
}

.row_down {
  animation: pulse-down 0.5s ease-in-out;
}

@keyframes pulse-up {
  0%,
  100% {
    background-color: var(--expo-alpha-3);
  }

  50% {
    background-color: var(--expo-alpha-2);
  }
}

@keyframes pulse-down {
  0%,
  100% {
    background-color: var(--red-alpha-3);
  }

  50% {
    background-color: var(--red-alpha-2);
  }
}

.real_time_table {
  table-layout: fixed;
  width: 100%;
}

.real_time_table th,
.real_time_table td {
  width: auto;
}

.real_time_table td.row_up,
.real_time_table td.row_down {
  background-color: transparent !important;
}

.active .toggle__switch_g::after {
  left: 50%;
  border: 1px solid var(--expo);
  width: 50%;
  background: var(--expo);
  box-shadow: 0 0 1px var(--expo);
}

.toggle__button_g .toggle__switch_g::after,
.toggle__button_g .toggle__switch_g::before {
  content: "";
}

.tooltip {
  transition: opacity 0.2s ease;
}
</style>
